import { useQuery } from '@tanstack/react-query'
import type { LucideIcon } from 'lucide-react'
import {
  Activity,
  BarChart3,
  Beaker,
  Bell,
  BrainCircuit,
  Building2,
  CalendarClock,
  ClipboardList,
  CreditCard,
  FileText,
  FlaskConical,
  GitBranch,
  History,
  Image,
  KeyRound,
  LayoutDashboard,
  Link2,
  ListChecks,
  Megaphone,
  PieChart,
  Plug,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  Tags,
  Timer,
  TrendingUp,
  Upload,
  UserCog,
  UserSearch,
  Users,
  Video,
  Workflow,
} from 'lucide-react'
import { useMemo } from 'react'
import { useAuth } from '@/lib/auth'
import { api } from '@/lib/http'

export interface NavItem {
  to: string
  label: string
  /** スマホの下のバーに出す2〜3文字。 */
  short?: string
  icon: LucideIcon
  /** 画面の検索で当てる言葉。 */
  keywords?: string
  description?: string
  end?: boolean
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

const OPERATE: NavGroup = {
  label: '運用',
  items: [
    { to: '/dashboard', label: 'ダッシュボード', icon: LayoutDashboard, keywords: 'dashboard 粗利 KPI チーム 運用者 アカウント だっしゅぼーど あらり', description: 'チーム・運用者・アカウント別の粗利とCPA' },
    { to: '/campaigns', label: 'キャンペーン別', icon: Megaphone, keywords: 'campaigns 停止 ON OFF 予算 明日回す きゃんぺーんべつ ていし はいしん', description: '今日の消化順に並べ、ON/OFF・明日回す' },
    { to: '/sales', label: '広告別', icon: TrendingUp, keywords: 'ads sales 広告 動画 クリエイティブ CR こうこくべつ', description: '広告・素材ごとの成績（強いCRを見つける）' },
    { to: '/next-day', label: '翌日配信予定', icon: CalendarClock, keywords: 'next-day 翌日 予約 0時 ON 予算 よくじつ はいしん よやく', description: '明日回す＋新規入稿＋配信中と、翌日の日予算の合計' },
    { to: '/explore', label: '階層で見る', icon: GitBranch, keywords: 'explore 階層 アカウント 広告セット 予算 かいそう', description: 'アカウント→キャンペーン→広告セット→広告' },
    { to: '/articles', label: '記事別粗利', icon: FileText, keywords: 'articles 記事 LP slug きじべつ あらり', description: 'LP（記事）ごとの粗利' },
  ],
}

const SUBMIT: NavGroup = {
  label: '入稿・自動化',
  items: [
    { to: '/submit', label: '一括入稿', icon: Upload, keywords: 'submit 入稿 まとめて 表 にゅうこう めた てぃっくとっく', description: '表計算のように行を並べて Meta・TikTok へ入稿', end: true },
    { to: '/rules', label: '自動ルール', icon: ShieldCheck, keywords: 'rules auto control 自動 停止 予算 ルール じどう せいぎょ', description: '条件で止める・つける・予算を変える' },
  ],
}

const REPORTS: NavGroup = {
  label: 'レポート',
  items: [{ to: '/reports', label: 'レポート', icon: PieChart, keywords: 'reports 日別 案件 着地 ヒートマップ セグメント スプレッドシート MCP れぽーと', description: '日別・案件×媒体・着地見込み・時間帯・セグメント・定期出力' }],
}

const CREATIVE: NavGroup = {
  label: '制作',
  items: [
    { to: '/scripts', label: '台本管理', icon: ClipboardList, keywords: 'scripts 台本 カンバン 承認 依頼 だいほん', description: '台本の作成・承認・制作の依頼・振り返り' },
    { to: '/opening-studio', label: '冒頭スタジオ', icon: Video, keywords: 'opening studio 冒頭 動画 生成 ぼうとう', description: '参照画像と指示から冒頭の動画を作る' },
    { to: '/creative', label: 'クリエイティブ生成', icon: Sparkles, keywords: 'creative 画像生成 範囲指定 くりえいてぃぶ', description: '画像の一部を選んで作り直す' },
    { to: '/lp-images', label: 'LP画像管理', icon: Image, keywords: 'lp images 画像 えるぴー', description: 'LP画像の管理と部分直し' },
    { to: '/hypotheses', label: '仮説', icon: BrainCircuit, keywords: 'hypotheses 切り口 冒頭 仮説 Oコード かせつ', description: '切り口・冒頭の仮説' },
    { to: '/kashika', label: '競合分析', icon: Search, keywords: 'kashika 競合 きょうごう', description: '競合の広告の調べ（取り込み）' },
  ],
}

const ANALYZE: NavGroup = {
  label: '分析',
  items: [
    { to: '/analytics/ocode', label: 'Oコード分析', icon: BarChart3, keywords: 'ocode 勝率 コンボ 冒頭 おーこーど', description: '冒頭の型ごとの勝率' },
    { to: '/experiments', label: '検証・実験', icon: FlaskConical, keywords: 'experiments 検証 実験 けんしょう', description: 'A/B の検証と結果' },
    { to: '/workflow', label: 'ワークフロー', icon: Workflow, keywords: 'workflow 業務フロー 進捗 わーくふろー', description: '台本から配信までの流れ' },
  ],
}

const TEAM: NavGroup = {
  label: 'チーム',
  items: [
    { to: '/schedule', label: '編集者スケジュール', icon: Users, keywords: 'schedule 編集者 稼働 chatwork へんしゅうしゃ', description: '編集者の稼働と依頼' },
    { to: '/crowdworks', label: 'CrowdWorks管理', icon: UserSearch, keywords: 'crowdworks 応募 トライアル くらうどわーくす', description: '応募者の選考とトライアル' },
    { to: '/skills', label: 'Skills管理', icon: ListChecks, keywords: 'skills スキル 自動化 すきる', description: '自動化の部品と動き' },
  ],
}

function settingsGroup(isAdmin: boolean): NavGroup {
  const items: NavItem[] = [
    { to: '/settings/connections', label: '連携', icon: Plug, keywords: 'connections 連携 meta tiktok google chatwork slack ウィザード れんけい', description: '媒体・ツールをつなぐ（設定ウィザード）' },
    { to: '/settings/ad-accounts', label: '広告アカウント', icon: Link2, keywords: 'ad accounts 広告アカウント CV MCV イベント 案件', description: 'CV・MCV の割り当てと案件' },
    { to: '/settings/cases', label: '案件と単価', icon: Tags, keywords: 'cases 案件 単価 手数料 目標 CPA あんけん たんか', description: '案件・手数料・目標・成果単価' },
    { to: '/settings/notifications', label: '通知', icon: Bell, keywords: 'notifications 通知 chatwork slack つうち', description: '通知先と受け取る出来事' },
  ]
  if (isAdmin) {
    items.push(
      { to: '/settings/members', label: 'メンバー', icon: Users, keywords: 'members メンバー アカウント 発行 招待 めんばー', description: 'アカウントの発行と役割' },
      { to: '/settings/directory', label: '名簿と命名', icon: UserCog, keywords: 'directory チーム 運用者コード 命名 めいぼ', description: 'チーム・運用者コード・キャンペーン名の読み方' },
      { to: '/settings/judgement', label: '判定の設定', icon: Settings2, keywords: 'judgement 当日OFF 判定 赤字 はんてい', description: '当日OFF候補・警戒の条件' },
      { to: '/settings/plan', label: 'プランと使用量', icon: CreditCard, keywords: 'plan プラン 上限 使用量 ぷらん', description: 'いまのプランと今月の使用量' },
      { to: '/settings/audit', label: '操作の記録', icon: History, keywords: 'audit 記録 ログ きろく', description: '誰がいつ何をしたか' },
    )
  }
  return { label: '設定', items }
}

const OPS: NavGroup = {
  label: '運営',
  items: [
    { to: '/ops', label: '運営の概要', icon: Activity, keywords: 'ops 運営 うんえい', description: '契約・アカウント・定期処理の状況', end: true },
    { to: '/ops/projects', label: '契約プロジェクト', icon: Building2, keywords: 'ops projects 契約 けいやく', description: '契約の作成・停止・メンバー' },
    { to: '/ops/plans', label: 'プラン', icon: CreditCard, keywords: 'ops plans プラン 上限', description: 'プランと上限' },
    { to: '/ops/users', label: 'アカウント', icon: Users, keywords: 'ops users アカウント 発行 運営', description: 'すべてのアカウントと運営の発行' },
    { to: '/ops/settings', label: '運営の設定', icon: KeyRound, keywords: 'ops settings アプリ 鍵 meta tiktok google anthropic gemini', description: '媒体アプリ・AI の鍵' },
    { to: '/ops/jobs', label: '定期処理', icon: Timer, keywords: 'ops jobs 定期 同期 ていき', description: '取り込み・予約・ルールの実行の記録' },
    { to: '/ops/audit', label: '操作の記録（全体）', icon: History, keywords: 'ops audit 記録', description: 'すべての操作の記録' },
  ],
}

interface CaseLite {
  id: string
  name: string
  cvSource: string
  status: string
}

/** いまの人・契約で出すメニュー。 */
export function useNavGroups(): NavGroup[] {
  const { me, isAdmin, canWrite } = useAuth()
  const active = me?.activeProject
  const cases = useQuery({
    queryKey: ['nav', 'cases', active?.id],
    queryFn: ({ signal }) => api.get<{ cases: CaseLite[] }>('/admin/cases', undefined, signal),
    enabled: Boolean(active),
    staleTime: 10 * 60_000,
  })
  return useMemo(() => {
    const groups: NavGroup[] = []
    if (active) {
      groups.push(OPERATE)
      const real = (cases.data?.cases ?? []).filter((c) => c.status === 'active' && c.cvSource !== 'media')
      if (real.length) {
        groups.push({
          label: '案件の実CPA',
          items: real.flatMap((c) => [
            { to: `/cases/${c.id}/ads`, label: `${c.name} 広告別`, icon: TrendingUp, keywords: `${c.name} 実CPA CPO utage`, description: `${c.name}の広告別 実CPA・CPO` },
            { to: `/cases/${c.id}/campaigns`, label: `${c.name} キャンペーン別`, icon: Megaphone, keywords: `${c.name} 実CPA CPO`, description: `${c.name}のキャンペーン別 実CPA・CPO` },
          ]),
        })
      }
      groups.push(SUBMIT, REPORTS, CREATIVE, ANALYZE, TEAM)
      if (canWrite || active.role === 'ops_view') groups.push(settingsGroup(isAdmin || active.role === 'ops_view'))
    }
    if (me?.user?.isOperator) groups.push(OPS)
    return groups
  }, [active, cases.data, canWrite, isAdmin, me?.user?.isOperator])
}

/** スマホの下のバー：勤務中によく開く5つ。 */
export const MOBILE_PRIMARY: NavItem[] = [
  { to: '/dashboard', label: 'ダッシュボード', short: '概況', icon: LayoutDashboard },
  { to: '/campaigns', label: 'キャンペーン別', short: '配信', icon: Megaphone },
  { to: '/next-day', label: '翌日配信', short: '翌日', icon: CalendarClock },
  { to: '/sales', label: '広告別', short: '広告', icon: TrendingUp },
  { to: '/rules', label: '自動ルール', short: 'ルール', icon: ShieldCheck },
]

export const MORE_ICON = Beaker
